import React from "react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

const InputField = ({
    label,
    id,
    name,
    type = "text",
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    helperText,
    required = false,
    disabled = false,
    className = "",
    ...props
}) => {
    const inputId = id || name;
    const hasError = !!error;
    
    const inputClasses = [
        "w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100",
        "focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200",
        "dark:border-slate-600 dark:placeholder:text-slate-400",
        hasError
            ? "border-red-500 focus:ring-red-500 focus:border-red-500 dark:border-red-500"
            : "border-slate-300 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600",
        disabled && "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed",
        className,
    ].filter(Boolean).join(" ");
    
    return (
        <div className="w-full">
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2"
                >
                    {label}
                    {required && (
                        <span className="text-red-500 ml-1" aria-label="required">
                            *
                        </span>
                    )}
                </label>
            )}
            <div className="relative">
                <input
                    id={inputId}
                    name={name}
                    type={type}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    className={inputClasses}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
                    {...props}
                />
                {hasError && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                )}
            </div>
            {hasError && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1" id={`${inputId}-error`}>
                    <ExclamationCircleIcon className="h-4 w-4" />
                    {error}
                </p>
            )}
            {helperText && !hasError && (
                <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400" id={`${inputId}-helper`}>
                    {helperText}
                </p>
            )}
        </div>
    );
};

export default InputField;
